import { LightningElement, wire } from "lwc";
import { ApolloQueryResult } from "@apollo/client";
import { MultiLabelAdapter, LabelTranslations } from "tbme/localization";
import { QueryAdapter } from "tbme/queryAdapter";
import {
  GetAgentblazerStatus,
  GetAgentblazerStatusVariables,
} from "./gql/GetAgentblazerStatus";
import { PublicProfile_trailheadStats } from "./gql/PublicProfile";
import { LearnerStatusLevel } from "./gql/LearnerStatusLevel";
import { LEARNER_STATUS_QUERY } from "./query";
import { chassis } from "tbme/localState";
import { getSharedUrlValues } from "../localState/urls";

type cardStates = "default" | "loading" | "loadFailed";

interface AgentblazerStatusLevel extends LearnerStatusLevel {
  displayText?: string;
}

const COMPONENT_LABELS = [
  "agentblazerStatusTitle",
  "agentblazerEmptyHeader",
  "agentblazerEmptySubheader",
  "agentblazerStartLearningButton",
  "failedToUpdate"
]

export default class extends LightningElement {
  private isSelf: boolean = undefined!;
  private trailheadAppUrl: string = undefined!;
  private variables: GetAgentblazerStatusVariables = {
    hasSlug: false,
  };

  connectedCallback() {
    const urls = getSharedUrlValues() as any;
    this.trailheadAppUrl = `${urls.trailhead}/agentblazer`;

    const chassisData = chassis();
    const username = chassisData?.profile?.username;
    this.isSelf = chassisData?.isSelf || false;

    this.variables = {
      ...this.variables,
      slug: username,
      hasSlug: !!username,
    };
  }

  @wire(QueryAdapter, {
    query: LEARNER_STATUS_QUERY,
    variables: "$variables",
  })
  handleResult(result: ApolloQueryResult<GetAgentblazerStatus>) {
    const { data, loading, error, errors } = result;

    if (loading) {
      this.cardState = "loading";
      return;
    }

    if (error || errors) {
      this.cardState = "loadFailed";
      return;
    }

    if (
      !data.profile ||
      data.profile.__typename !== "PublicProfile" ||
      !data.profile.trailheadStats
    ) {
      this.cardState = "loadFailed";
      return;
    }

    this.updateAgentblazerStatus(data.profile.trailheadStats);
  }

  private cardState: cardStates = "loading";
  private agentblazerStatusLevels: AgentblazerStatusLevel[] = [];

  @wire(MultiLabelAdapter, {
    labels: COMPONENT_LABELS,
  })
  private labels: LabelTranslations = {};

  updateAgentblazerStatus = ({
    learnerStatusLevels,
  }: PublicProfile_trailheadStats) => {
    this.cardState = "default";

    if (!learnerStatusLevels || learnerStatusLevels.length === 0) {
      this.agentblazerStatusLevels = [];
      return;
    }

    // Filter for Agentblazer status only
    const agentblazerLevels = learnerStatusLevels.filter(
      (record) => record?.statusName === "Agentblazer"
    ) as AgentblazerStatusLevel[];

    // Group by edition and get the highest level with progress == 100.0 for each edition
    const editionMap = new Map<string, AgentblazerStatusLevel>();

    agentblazerLevels.forEach((record) => {
      if (record.progress === 100.0) {
        const edition = record.edition || "";
        const existing = editionMap.get(edition);

        if (!existing || record.level > existing.level) {
          editionMap.set(edition, record);
        }
      }
    });

    // Convert to array and sort by completedAt DESC
    const sortedLevels = Array.from(editionMap.values())
      .map((record) => {
        return {
          ...record,
          displayText: `${record.title || ""} ${record.edition || ""}`.trim(),
        };
      })
      .sort((a, b) => {
        if (!a.completedAt && !b.completedAt) return 0;
        if (!a.completedAt) return 1;
        if (!b.completedAt) return -1;
        return (
          new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
        );
      });

    this.agentblazerStatusLevels = sortedLevels;
  };

  private get hasActivity() {
    return (
      !this.isLoading &&
      !this.hasError &&
      this.agentblazerStatusLevels.length > 0
    );
  }
  
  private get hasError() {
    return this.cardState === "loadFailed";
  }

  get isVisible() {
    if (this.isSelf) {
      return this.hasError || !this.isLoading
    }
    return this.hasActivity;
  }

  private get isLoading() {
    return this.cardState === "loading";
  }
}
